import React, { useState, useEffect, useRef, useCallback } from "react";

import Lottie from "react-lottie";

import confettiAnimation from "../assets/confetti.json";
import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";
import "./ManageCategories.css";

const ManageCategories = ({
  categories,
  isOpen,
  onClose,
  onUpdate,
  profile,
}) => {
  const [selectedCategories, setSelectedCategories] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [showPinOverlay, setShowPinOverlay] = useState(false);
  const [selectedCategoryForPin, setSelectedCategoryForPin] = useState(null);
  const [pinCode, setPinCode] = useState(["", "", "", ""]);
  const [lockedCategories, setLockedCategories] = useState({});
  const [showOptionsOverlay, setShowOptionsOverlay] = useState(false);
  const [pinMode, setPinMode] = useState("set"); // "set" or "verify"
  const [pinError, setPinError] = useState(false);
  const [selectedCategoryForOptions, setSelectedCategoryForOptions] =
    useState(null);
  const longPressTimer = useRef(null);
  const pinInputRefs = useRef([]);
  const draggedElement = useRef(null);
  const hasInitialized = useRef(false);
  const [lastDraggedCategory, setLastDraggedCategory] = useState(null);
  const [originalApiOrder, setOriginalApiOrder] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen && categories && categories.length > 0) {
      // Only initialize once when dialog opens
      if (!hasInitialized.current) {
        // Store the original API order
        const apiOrder = categories.map((cat) => cat.id);

        setOriginalApiOrder(apiOrder);

        // Check if profile has saved settings
        const profileSettings = profile?.preferences?.liveTvSettings;

        if (profileSettings) {
          // Load settings from profile
          setSelectedCategories(profileSettings.categoryVisibility || {});
          setCategoryOrder(profileSettings.categoryOrder || apiOrder);
          setLockedCategories(profileSettings.lockedCategories || {});
        } else {
          // Fall back to localStorage
          // Initialize selected categories (all checked by default)
          const initialSelected = {};

          categories.forEach((cat) => {
            // All categories should be manageable
            initialSelected[cat.id] =
              localStorage.getItem(`category_hidden_${cat.id}`) !== "true";
          });
          setSelectedCategories(initialSelected);

          // Initialize category order
          const savedOrder = localStorage.getItem("category_order");

          if (savedOrder) {
            setCategoryOrder(JSON.parse(savedOrder));
          } else {
            setCategoryOrder(apiOrder);
          }

          // Load locked categories
          const savedLocked = localStorage.getItem("locked_categories");

          if (savedLocked) {
            setLockedCategories(JSON.parse(savedLocked));
          }
        }

        hasInitialized.current = true;
      }
    }

    // Reset initialization flag when dialog closes
    if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen, categories, profile]); // Include profile in dependencies

  const handleCheckAll = () => {
    const newSelected = {};

    categories.forEach((cat) => {
      newSelected[cat.id] = true;
    });
    setSelectedCategories(newSelected);
  };

  const handleUncheckAll = () => {
    const newSelected = {};

    categories.forEach((cat) => {
      newSelected[cat.id] = false;
    });
    setSelectedCategories(newSelected);
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleLongPressStart = (e, category) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't start a new long press if options overlay is already showing
    if (showOptionsOverlay) {
      return;
    }

    // Clear any existing timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    longPressTimer.current = setTimeout(() => {
      // Show options overlay instead of context menu
      setSelectedCategoryForOptions(category);
      setShowOptionsOverlay(true);
      longPressTimer.current = null;
    }, 500);
  };

  const handleLongPressEnd = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // If the options overlay is showing, don't do anything - let it stay open
    if (showOptionsOverlay) {
      return;
    }

    // Only clear timer if we haven't shown the menu yet
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleOptionSelect = (action) => {
    const category = selectedCategoryForOptions;

    setShowOptionsOverlay(false);
    setSelectedCategoryForOptions(null);

    // Clear any remaining timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    if (action === "drag" && category) {
      // Start dragging
      setIsDragging(true);
      setDraggedItem(category);

      // Create floating element at center of screen
      const categoryRow = document.querySelector(
        `[data-category-id="${category.id}"]`
      );

      if (categoryRow) {
        // Get the original width of the category row
        const originalWidth = categoryRow.getBoundingClientRect().width;

        const element = categoryRow.cloneNode(true);

        element.classList.add("dragging-item");
        element.style.position = "fixed";
        element.style.pointerEvents = "none";
        element.style.zIndex = "10000";
        element.style.width = `${originalWidth * 1.3}px`;
        element.style.border = "4px solid #6495ED";
        element.style.transform = "rotate(20deg)";
        element.style.opacity = "0.95";
        element.style.left = `${window.innerWidth / 2}px`;
        element.style.top = `${window.innerHeight / 2}px`;
        element.style.filter = "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.8))";
        document.body.appendChild(element);
        draggedElement.current = element;

        // Update position on mouse move
        const handleMouseMove = (e) => {
          if (draggedElement.current) {
            draggedElement.current.style.left = `${e.clientX - 100}px`;
            draggedElement.current.style.top = `${e.clientY - 20}px`;

            // Auto-scroll when dragging near edges
            const contentArea = document.querySelector(
              ".manage-categories-content"
            );

            if (contentArea) {
              const rect = contentArea.getBoundingClientRect();
              const scrollThreshold = 50;
              const scrollSpeed = 10;

              // Scroll down when near bottom
              if (e.clientY > rect.bottom - scrollThreshold) {
                contentArea.scrollTop += scrollSpeed;
              }
              // Scroll up when near top
              else if (e.clientY < rect.top + scrollThreshold) {
                contentArea.scrollTop -= scrollSpeed;
              }
            }

            // Check which category we're hovering over
            const elementBelow = document.elementFromPoint(
              e.clientX,
              e.clientY
            );
            const categoryRow = elementBelow?.closest(".category-row");

            if (categoryRow) {
              const categoryId = categoryRow.getAttribute("data-category-id");
              const hoverCategory = categories.find(
                (cat) => cat.id === categoryId
              );

              if (hoverCategory && hoverCategory.id !== category.id) {
                setDraggedOverItem(hoverCategory);
              }
            } else {
              setDraggedOverItem(null);
            }
          }
        };

        const handleMouseUp = (e) => {
          // Check if we're dropping on a category
          const elementBelow = document.elementFromPoint(e.clientX, e.clientY);
          const categoryRow = elementBelow?.closest(".category-row");

          if (categoryRow) {
            const categoryId = categoryRow.getAttribute("data-category-id");
            const dropCategory = categories.find(
              (cat) => cat.id === categoryId
            );

            if (dropCategory && dropCategory.id !== category.id) {
              // Reorder the categories
              const newOrder = [...categoryOrder];
              const draggedIndex = newOrder.indexOf(category.id);
              const dropIndex = newOrder.indexOf(dropCategory.id);

              if (draggedIndex !== -1 && dropIndex !== -1) {
                newOrder.splice(draggedIndex, 1);
                newOrder.splice(dropIndex, 0, category.id);
                setCategoryOrder(newOrder);
                // Track the category that was just dragged
                setLastDraggedCategory(category.id);

                // Show confetti behind the dropped item
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 2000);
              }
            }
          }

          handleDragEnd();
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }
    } else if (action === "pin" && category) {
      // Show PIN dialog
      handlePinCategory(category);
    }
  };

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedItem(null);
    setDraggedOverItem(null);

    if (draggedElement.current) {
      draggedElement.current.remove();
      draggedElement.current = null;
    }
  }, []);

  const handlePinCategory = (category) => {
    setSelectedCategoryForPin(category);
    setShowPinOverlay(true);
    setPinCode(["", "", "", ""]);
    setPinError(false);

    // Check if category already has a PIN
    if (lockedCategories[category.id]) {
      setPinMode("verify");
    } else {
      setPinMode("set");
    }

    setTimeout(() => {
      if (pinInputRefs.current[0]) {
        pinInputRefs.current[0].focus();
      }
    }, 100);
  };

  const handlePinInput = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newPin = [...pinCode];

      newPin[index] = value;
      setPinCode(newPin);
      setPinError(false);

      // Auto-focus next input
      if (value && index < 3) {
        pinInputRefs.current[index + 1]?.focus();
      }

      // Check if PIN is complete
      if (index === 3 && value) {
        const fullPin = newPin.join("");

        if (fullPin.length === 4) {
          if (pinMode === "verify") {
            // Verify existing PIN
            if (fullPin === lockedCategories[selectedCategoryForPin.id]) {
              // Correct PIN - remove lock
              const newLocked = { ...lockedCategories };

              delete newLocked[selectedCategoryForPin.id];
              setLockedCategories(newLocked);
              localStorage.setItem(
                "locked_categories",
                JSON.stringify(newLocked)
              );
              setShowPinOverlay(false);
              debugLog(
                `PIN removed for category: ${selectedCategoryForPin.name}`
              );
            } else {
              // Wrong PIN
              setPinError(true);
              setPinCode(["", "", "", ""]);
              setTimeout(() => {
                if (pinInputRefs.current[0]) {
                  pinInputRefs.current[0].focus();
                }
              }, 100);
            }
          } else {
            // Set new PIN
            const newLocked = {
              ...lockedCategories,
              [selectedCategoryForPin.id]: fullPin,
            };

            setLockedCategories(newLocked);
            localStorage.setItem(
              "locked_categories",
              JSON.stringify(newLocked)
            );
            setShowPinOverlay(false);
            debugLog(`PIN set for category: ${selectedCategoryForPin.name}`);
          }
        }
      }
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pinCode[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
  };

  const handleUpdate = async () => {
    // Check if the order has changed by comparing with localStorage
    const savedOrder = localStorage.getItem("category_order");
    const previousOrder = savedOrder
      ? JSON.parse(savedOrder)
      : categories.map((cat) => cat.id);
    const orderChanged =
      JSON.stringify(previousOrder) !== JSON.stringify(categoryOrder);

    // Only pass the last dragged category if the order changed
    const draggedCategoryToHighlight = orderChanged
      ? lastDraggedCategory
      : null;

    // Save visibility settings to localStorage
    Object.entries(selectedCategories).forEach(([categoryId, isVisible]) => {
      localStorage.setItem(
        `category_hidden_${categoryId}`,
        !isVisible ? "true" : "false"
      );
    });

    // Save order to localStorage
    localStorage.setItem("category_order", JSON.stringify(categoryOrder));

    // Save settings to profile if profile exists
    if (profile && profile.id) {
      try {
        const profileSettings = {
          categoryVisibility: selectedCategories,
          categoryOrder,
          lockedCategories,
        };

        // Store in profile's preferences or settings
        const updatedProfile = {
          ...profile,
          preferences: {
            ...(profile.preferences || {}),
            liveTvSettings: profileSettings,
          },
        };

        await apiService.webPlayerUpdateProfile(profile.id, updatedProfile);
        debugLog("Saved category settings to profile");
      } catch (error) {
        console.error("Error saving settings to profile:", error);
      }
    }

    if (onUpdate) {
      onUpdate({
        visible: selectedCategories,
        order: categoryOrder,
        locked: lockedCategories,
        draggedCategory: draggedCategoryToHighlight, // Pass only the last dragged category
        orderChanged,
      });
    }

    // Clear the last dragged category after update
    setLastDraggedCategory(null);
    onClose();
  };

  const handleResetOrder = () => {
    // Reset to the original API order
    setCategoryOrder(originalApiOrder);
    debugLog("Reset category order to original API order:", originalApiOrder);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;

      if (showOptionsOverlay) {
        setShowOptionsOverlay(false);
        setSelectedCategoryForOptions(null);
      } else if (isDragging) {
        handleDragEnd();
      } else if (showPinOverlay) {
        setShowPinOverlay(false);
      } else if (isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isDragging,
    showPinOverlay,
    showOptionsOverlay,
    isOpen,
    onClose,
    handleDragEnd,
  ]);

  if (!isOpen) return null;

  const orderedCategories = categoryOrder
    .map((id) => categories.find((cat) => cat.id === id))
    .filter(Boolean);

  return (
    <div className="manage-categories-overlay">
      <div className="manage-categories-modal">
        <div className="manage-categories-header">
          <div className="manage-categories-header-content">
            <h2>Manage Categories</h2>
            <span className="manage-categories-hint">
              Show/Hide Categories
              <br />
              Long press to Reorder and set PIN
            </span>
          </div>
          <button className="manage-categories-close" onClick={onClose}>
            <i className="fa-solid fa-times" />
          </button>
        </div>

        <div className="manage-categories-content">
          <div className="categories-list">
            {orderedCategories.map((category) => (
              <div
                aria-label={`Options for ${category.name}`}
                className={`category-row ${isDragging && draggedItem?.id === category.id ? "dragging" : ""}
                           ${draggedOverItem?.id === category.id ? "drag-over" : ""}
                           ${!selectedCategories[category.id] ? "unchecked" : ""}`}
                data-category-id={category.id}
                key={category.id}
                role="button"
                style={{ position: "relative" }}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedCategoryForOptions(category);
                    setShowOptionsOverlay(true);
                  }
                }}
                onMouseDown={(e) => {
                  // Only handle if not clicking on checkbox and not dragging
                  if (
                    !e.target.closest(".category-checkbox") &&
                    !isDragging &&
                    !showOptionsOverlay
                  ) {
                    handleLongPressStart(e, category);
                  }
                }}
                onMouseLeave={(e) => {
                  // Only clear timer if we haven't shown overlay yet and not dragging
                  if (
                    !showOptionsOverlay &&
                    !isDragging &&
                    !e.target.closest(".category-checkbox")
                  ) {
                    handleLongPressEnd(e);
                  }
                }}
                onMouseUp={(e) => {
                  // Only handle if not clicking on checkbox and not dragging
                  if (
                    !e.target.closest(".category-checkbox") &&
                    !isDragging &&
                    !showOptionsOverlay
                  ) {
                    handleLongPressEnd(e);
                  }
                }}
                onTouchCancel={(e) => {
                  if (
                    !e.target.closest(".category-checkbox") &&
                    !isDragging &&
                    !showOptionsOverlay
                  ) {
                    handleLongPressEnd(e);
                  }
                }}
                onTouchEnd={(e) => {
                  if (
                    !e.target.closest(".category-checkbox") &&
                    !isDragging &&
                    !showOptionsOverlay
                  ) {
                    handleLongPressEnd(e);
                  }
                }}
                onTouchStart={(e) => {
                  // Touch events for mobile
                  if (
                    !e.target.closest(".category-checkbox") &&
                    !isDragging &&
                    !showOptionsOverlay
                  ) {
                    handleLongPressStart(e, category);
                  }
                }}
              >
                <button
                  className="category-checkbox"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleCategoryToggle(category.id);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  {selectedCategories[category.id] && (
                    <i className="fa-solid fa-check" />
                  )}
                </button>

                <span className="category-row-name">{category.name}</span>

                {lockedCategories[category.id] && (
                  <i className="fa-solid fa-lock category-lock-icon" />
                )}

                {isDragging && (
                  <i className="fa-solid fa-bars category-drag-icon" />
                )}

                {/* Confetti animation behind the item when it's being placed */}
                {showConfetti && lastDraggedCategory === category.id && (
                  <div
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      pointerEvents: "none",
                      zIndex: -1,
                    }}
                  >
                    <Lottie
                      height={300}
                      options={{
                        loop: false,
                        autoplay: true,
                        animationData: confettiAnimation,
                        rendererSettings: {
                          preserveAspectRatio: "xMidYMid slice",
                        },
                      }}
                      width={300}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="manage-categories-footer">
          <button className="manage-btn" onClick={handleCheckAll}>
            Check All
          </button>
          <button className="manage-btn" onClick={handleUncheckAll}>
            Uncheck All
          </button>
          <button className="manage-btn" onClick={handleResetOrder}>
            Reset Order
          </button>
          <button
            className="manage-btn manage-btn-primary"
            onClick={handleUpdate}
          >
            Update
          </button>
        </div>
      </div>

      {/* Options Overlay */}
      {showOptionsOverlay && (
        <div className="options-overlay">
          <div className="options-container">
            <button
              className="options-close"
              onClick={() => {
                setShowOptionsOverlay(false);
                setSelectedCategoryForOptions(null);
              }}
            >
              <i className="fa-solid fa-times" />
            </button>
            <h3 className="options-title">
              {selectedCategoryForOptions?.name}
            </h3>
            <div className="options-buttons">
              <button
                className="option-button"
                onClick={() => handleOptionSelect("pin")}
              >
                <i className="fa-solid fa-lock" />
                <span>
                  {lockedCategories[selectedCategoryForOptions?.id]
                    ? "Enter PIN"
                    : "Set PIN"}
                </span>
              </button>
              <button
                className="option-button"
                onClick={() => handleOptionSelect("drag")}
              >
                <i className="fa-solid fa-arrows-up-down" />
                <span>Drag to Reorder</span>
              </button>
            </div>
            <button
              className="options-cancel"
              onClick={() => {
                setShowOptionsOverlay(false);
                setSelectedCategoryForOptions(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* PIN Overlay */}
      {showPinOverlay && (
        <div className="pin-overlay">
          <div className="pin-modal">
            <button
              className="pin-close"
              onClick={() => setShowPinOverlay(false)}
            >
              <i className="fa-solid fa-times" />
            </button>
            <h3>{pinMode === "verify" ? "Enter PIN" : "Create PIN"}</h3>
            <p>
              {pinMode === "verify"
                ? pinError
                  ? "Incorrect PIN. Try again."
                  : "Enter PIN to unlock this category"
                : "This PIN will protect this category"}
            </p>

            <div className="pin-inputs">
              {[0, 1, 2, 3].map((index) => (
                <input
                  className={`pin-input ${pinError ? "pin-error" : ""}`}
                  key={index}
                  maxLength="1"
                  ref={(el) => (pinInputRefs.current[index] = el)}
                  type="password"
                  value={pinCode[index]}
                  onChange={(e) => handlePinInput(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                />
              ))}
            </div>

            <button
              className="pin-cancel"
              onClick={() => setShowPinOverlay(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCategories;
